import React from 'react'
import { Event } from 'app/types/authorization'
import { Addon } from 'app/types/items'
import { useTranslation } from 'next-i18next'
import { ItemPicker } from '../../components/old/ItemsPicker'
import { CompanyProfile } from 'app/types/organization'
import { KioskItemPicker } from '../../components/old/KioskItemPicker'
import { RestaurantItemPicker } from '../../components/old/RestuarantItemPicker'

// Define the props for the Items component
interface ItemsProps {
  event: Event
  items: Addon[]
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => void
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  shouldHaveTickets: boolean
  shouldHaveItems: boolean
  shouldHaveKiosk: boolean
  shouldHaveRestaurant: boolean
  currentStep: number
  companyProfile: CompanyProfile | null
}

// Component for rendering the header section
const Header: React.FC<{
  title: string
  companyProfile: CompanyProfile | null
}> = ({ title, companyProfile }) => (
  <div className="flex flex-row justify-between pt-8">
    <div className="w-screen px-4 sm:w-2/3 sm:px-0">
      <h1
        className="text-xl font-bold tracking-tight text-gray-900 sm:text-4xl"
        style={{
          fontFamily: companyProfile?.font?.name
            ? companyProfile.font.name
            : undefined,
        }}
      >
        {title}
      </h1>
    </div>
  </div>
)

// Component for rendering the product grid section
const ProductGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('common')
  return (
    <section aria-labelledby="products-heading" className="mt-4 sm:mt-8">
      <h2 id="products-heading" className="sr-only">
        {t('products')}
      </h2>
      {children}
    </section>
  )
}

// Component for Kiosk Items
const KioskItems: React.FC<ItemsProps> = ({
  event,
  items,
  refetchCart,
  setShowSuccessMessage,
  setShowFailureMessage,
  companyProfile,
}) => {
  const { t } = useTranslation('common')

  return (
    <div className="min-h-screen">
      <div className="min-h-screen">
        <div className="mx-0 sm:mx-10">
          <Header title={t('kiosk')} companyProfile={companyProfile} />
          <ProductGrid>
            <KioskItemPicker
              eventId={event.sk}
              items={items}
              refetchCart={refetchCart}
              setShowSuccessMessage={setShowSuccessMessage}
              setShowFailureMessage={setShowFailureMessage}
              companyProfile={companyProfile}
            />
          </ProductGrid>
        </div>
      </div>
    </div>
  )
}

// Component for Restaurant Items
const RestaurantItems: React.FC<ItemsProps> = ({
  event,
  items,
  refetchCart,
  setShowSuccessMessage,
  setShowFailureMessage,
  companyProfile,
}) => {
  const { t } = useTranslation('common')

  return (
    <div className="min-h-screen">
      <div className="min-h-screen">
        <div className="mx-0 sm:mx-10">
          <Header title={t('restaurant')} companyProfile={companyProfile} />
          <ProductGrid>
            <RestaurantItemPicker
              eventId={event.sk}
              items={items}
              refetchCart={refetchCart}
              setShowSuccessMessage={setShowSuccessMessage}
              setShowFailureMessage={setShowFailureMessage}
              companyProfile={companyProfile}
            />
          </ProductGrid>
        </div>
      </div>
    </div>
  )
}

// Component for Merchandise Items
const MerchItems: React.FC<ItemsProps> = ({
  event,
  items,
  refetchCart,
  setShowSuccessMessage,
  setShowFailureMessage,
  companyProfile,
}) => {
  const { t } = useTranslation('common')

  return (
    <div className="min-h-screen">
      <div className="min-h-screen">
        <div className="mx-0 sm:mx-10">
          <Header title={t('merchandise')} companyProfile={companyProfile} />
          <ProductGrid>
            <ItemPicker
              eventId={event.sk}
              items={items}
              refetchCart={refetchCart}
              setShowSuccessMessage={setShowSuccessMessage}
              setShowFailureMessage={setShowFailureMessage}
              companyProfile={companyProfile}
            />
          </ProductGrid>
        </div>
      </div>
    </div>
  )
}

// Main Items component
export const Items: React.FC<ItemsProps> = ({
  event,
  items,
  refetchCart,
  setCurrentStep,
  setShowSuccessMessage,
  setShowFailureMessage,
  currentStep,
  companyProfile,
  shouldHaveTickets,
  shouldHaveItems,
  shouldHaveKiosk,
  shouldHaveRestaurant,
}) => {
  const steps: JSX.Element[] = []

  // Add Restaurant Items if applicable
  if (shouldHaveRestaurant) {
    steps.push(
      <RestaurantItems
        key={'restaurant'}
        event={event}
        setCurrentStep={setCurrentStep}
        currentStep={currentStep}
        items={items}
        refetchCart={refetchCart}
        setShowSuccessMessage={setShowSuccessMessage}
        setShowFailureMessage={setShowFailureMessage}
        shouldHaveTickets={shouldHaveTickets}
        shouldHaveItems={shouldHaveItems}
        shouldHaveKiosk={shouldHaveKiosk}
        shouldHaveRestaurant={shouldHaveRestaurant}
        companyProfile={companyProfile}
      />
    )
  }

  // Add Kiosk Items if applicable
  if (shouldHaveKiosk) {
    steps.push(
      <KioskItems
        key={'kiosk'}
        event={event}
        setCurrentStep={setCurrentStep}
        currentStep={currentStep}
        items={items}
        refetchCart={refetchCart}
        setShowSuccessMessage={setShowSuccessMessage}
        setShowFailureMessage={setShowFailureMessage}
        shouldHaveTickets={shouldHaveTickets}
        shouldHaveItems={shouldHaveItems}
        shouldHaveKiosk={shouldHaveKiosk}
        shouldHaveRestaurant={shouldHaveRestaurant}
        companyProfile={companyProfile}
      />
    )
  }

  // Add Merchandise Items if applicable
  if (shouldHaveItems) {
    steps.push(
      <MerchItems
        key={'merch'}
        event={event}
        setCurrentStep={setCurrentStep}
        currentStep={currentStep}
        items={items}
        refetchCart={refetchCart}
        setShowSuccessMessage={setShowSuccessMessage}
        setShowFailureMessage={setShowFailureMessage}
        shouldHaveTickets={shouldHaveTickets}
        shouldHaveItems={shouldHaveItems}
        shouldHaveKiosk={shouldHaveKiosk}
        shouldHaveRestaurant={shouldHaveRestaurant}
        companyProfile={companyProfile}
      />
    )
  }

  // Calculate the correct index based on the current step and whether tickets are included
  let index = currentStep - 1
  if (shouldHaveTickets) {
    index = index - 1
  }

  // Return the appropriate step component or null if index is out of bounds
  return steps[index] ?? null
}
